import { useHistory, useParams } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Typography from '@material-ui/core/Typography'
import CloudUpload from '@material-ui/icons/CloudUpload'
import Delete from '@material-ui/icons/Delete'
import Visibility from '@material-ui/icons/Visibility'
import React, { useState, useEffect, useRef, useCallback } from 'react'

import { authUtils, constants, dates } from '../../utils'
import {
  ErrorPlaceholder,
  LoadingPlaceholder,
} from '../../components/Placeholders'
import { PageHeader } from '../../components/Layout'
import { ScheduleDetails } from '../../components/Schedules'
import { documentsService, schedulesService } from '../../services'

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '12px',
  },
  section: {
    marginTop: '32px',
  },
  sectionTitle: {
    marginBottom: '20px',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
  },
  fileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '2px solid #ccc',
    fontWeight: 'bold',
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  fileDate: {
    flex: 1,
    textAlign: 'center',
  },
  fileActions: {
    flex: 1,
    textAlign: 'right',
  },
}))

export default () => {
  const classes = useStyles()
  const history = useHistory()

  const { scheduleId } = useParams()

  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [schedule, setSchedule] = useState([])
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)

  const fetchFiles = useCallback(async () => {
    const patientId = authUtils.getUserId()
    const result = await documentsService.list(scheduleId, patientId)
    if (result.success) {
      setFiles(result.payload)
    }
  }, [scheduleId])

  const handleUploadClick = () => {
    fileInputRef.current.click()
  }

  const handleFileChange = async event => {
    const selectedFiles = Array.from(event.target.files)
    event.target.value = ''

    if (!selectedFiles.length) return

    setUploading(true)
    const patientId = authUtils.getUserId()
    const result = await documentsService.upload(selectedFiles, scheduleId, patientId)
    setUploading(false)

    if (result.success) {
      fetchFiles()
    } else {
      alert(result.error)
    }
  }

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Tem certeza que deseja deletar ${fileName}?`)) return
    const result = await documentsService.remove(fileId)
    if (result.success) {
      setFiles(prev => prev.filter(f => f.id !== fileId))
    } else {
      alert(`Erro ao deletar ${fileName}`)
    }
  }

  const goToPrint = () =>
    window
      .open(constants.routes.schedules.print.path(scheduleId), '_blank')
      .focus()

  const tryToGetSchedule = async () => {
    setLoading(true)
    const { success, payload } = await schedulesService.show(scheduleId)

    if (success) {
      setSchedule(payload.schedule)
      setError(false)
    } else {
      setError(true)
    }

    setLoading(false)
  }

  useEffect(() => {
    tryToGetSchedule()
  }, [])

  useEffect(() => {
    if (!loading && !error) {
      fetchFiles()
    }
  }, [loading, error, fetchFiles])

  return (
    <>
      <PageHeader
        title={constants.routes.schedules.details.title}
        buttonText="Voltar"
        onClick={() => history.push(constants.routes.schedules.list.path)}
      />
      {loading ? (
        <LoadingPlaceholder />
      ) : error ? (
        <ErrorPlaceholder onClick={tryToGetSchedule} />
      ) : (
        <>
          <ScheduleDetails schedule={schedule} showVideoConference />
          <Button
            className={`${classes.button} no-print`}
            color="default"
            variant="contained"
            onClick={goToPrint}
            style={{ marginRight: 8 }}
          >
            Imprimir
          </Button>
          {!schedule.is_past && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                className={`${classes.button} no-print`}
                color="default"
                variant="contained"
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Enviando...' : 'Upload'}
              </Button>
            </>
          )}

          {files.length > 0 && (
            <section className={classes.section}>
              <Divider />
              <Typography className={classes.sectionTitle} variant="h5">Arquivos enviados</Typography>
              <div className={classes.fileHeader}>
                <Typography className={classes.fileName} variant="body2">Nome do arquivo</Typography>
                <Typography className={classes.fileDate} variant="body2">Data da criação</Typography>
                <Typography className={classes.fileActions} variant="body2">Ações</Typography>
              </div>
              {files.map(file => (
                <div key={file.id} className={classes.fileItem}>
                  <Typography className={classes.fileName} variant="body2">
                    {file.name}
                  </Typography>
                  <Typography className={classes.fileDate} variant="body2">
                    {dates.format(file.created_at, 'DD/MM/YY [às] LT')}
                  </Typography>
                  <div className={classes.fileActions}>
                    <IconButton
                      size="small"
                      onClick={() => window.open(file.url, '_blank')}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFile(file.id, file.name)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </>
  )
}
